import type { QboEnvironment, QboMirrorStatus, QboWriteStatus, BooksSwitchStatus } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, QueryView } from '../components/ui';
import { formatDate, formatMoney, todayIso, whenAt } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';

/** What the app asks a person to do by hand in QuickBooks, then mark done there (decision 186; as the server's list). */
const DONE_THERE_NEEDS = ['deposited', 'applied_elsewhere', 'memo_before_switch', 'changed_in_quickbooks'];
const ERRORS = ['declined', 'intuit', 'state', 'signin', 'exchange', 'production', 'company'] as const;
/** QuickBooks' record types as the import counts them, named in the office's words */
const KINDS = new Set(['Term', 'Account', 'Customer', 'Vendor', 'Item', 'PaymentMethod', 'Invoice', 'CreditMemo', 'SalesReceipt', 'RefundReceipt', 'Payment', 'Bill', 'BillPayment', 'VendorCredit', 'Purchase', 'Deposit', 'Transfer', 'JournalEntry']);

/**
 * Administration → QuickBooks (decision 85, owner): connect the company once, see which one is connected and until
 * when, check that the app can read it, disconnect. Read-only for now; the import builds on this connection.
 */
export function QuickBooksPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [env, setEnv] = useState<QboEnvironment>('production');
  // while an import runs, look again every few seconds
  const status = useQuery({ queryKey: qk.qboStatus, queryFn: ({ signal }) => api.qboStatus(signal), refetchInterval: (q) => (q.state.data?.history.running ? 4000 : false) });
  const pull = useMutation({ mutationFn: (full: boolean) => api.qboPull(full), onSettled: () => void qc.invalidateQueries({ queryKey: qk.qboStatus }) });
  const check = useQuery({ queryKey: qk.qboCheck, queryFn: ({ signal }) => api.qboCheck(signal), enabled: false, retry: false });
  const connect = useMutation({
    mutationFn: (which: QboEnvironment) => api.qboConnect(which),
    onSuccess: ({ url }) => window.location.assign(url),
  });
  const disconnect = useMutation({
    mutationFn: () => api.qboDisconnect(),
    onSuccess: () => {
      qc.removeQueries({ queryKey: qk.qboCheck });
      void qc.invalidateQueries({ queryKey: qk.qboStatus });
    },
  });
  const error = params.get('error');
  const justConnected = params.get('connected') === '1';
  const dismiss = () => setParams({}, { replace: true });
  // the stored tokens no longer work (Intuit revoked them, or the app's key changed): the card must not still read "connected"
  const refused = (check.error instanceof ApiError && check.error.status === 409) || (pull.error instanceof ApiError && pull.error.status === 409);
  const day = (iso: string | null) => (iso ? formatDate(todayIso(new Date(iso)), intlLocale, 'date') : '—');

  return (
    <div className="desk-page" data-testid="quickbooks">
      <header className="desk-head">
        <div>
          <h1>{t('qboTitle')}</h1>
          <p className="muted">{t('qboHint')}</p>
        </div>
      </header>
      {justConnected ? (
        <p className="ok-band" role="status">
          {t('qboConnectedNow')}{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={dismiss}>
            {t('dismiss')}
          </button>
        </p>
      ) : null}
      {error ? (
        <p className="warn-band" role="alert">
          {ERRORS.includes(error as (typeof ERRORS)[number]) ? t(`qboError_${error}` as AppMessageKey) : t('qboError_intuit')}{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={dismiss}>
            {t('dismiss')}
          </button>
        </p>
      ) : null}
      <QueryView query={status}>
        {(s) =>
          !s.configured ? (
            <section className="panel stack-sm">
              <h2>{t('qboNotSetTitle')}</h2>
              <p>{t('qboNotSet')}</p>
            </section>
          ) : s.connection ? (
            <>
              {refused ? (
                <p className="warn-band" role="alert" data-testid="qbo-reconnect">
                  {t('qboReconnectNeeded')}{' '}
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => connect.mutate(s.connection!.environment)} disabled={connect.isPending}>
                    {t('qboReconnect')}
                  </button>
                  {connect.error ? <ErrorBox error={connect.error} /> : null}
                </p>
              ) : null}
              <section className="panel stack-sm" data-testid="qbo-connection">
                <h2>{t('qboConnection')}</h2>
                <dl className="facts plain">
                  <div className="fact">
                    <dt>{t('qboCompany')}</dt>
                    <dd>{s.connection.companyName ?? '—'}</dd>
                  </div>
                  <div className="fact">
                    <dt>{t('qboBooks')}</dt>
                    <dd>{s.connection.environment === 'production' ? t('qboEnv_production') : t('qboEnv_sandbox')}</dd>
                  </div>
                  <div className="fact">
                    <dt>{t('qboConnectedOn')}</dt>
                    <dd>
                      {day(s.connection.connectedAt)}
                      {s.connection.connectedByName ? ` · ${s.connection.connectedByName}` : ''}
                    </dd>
                  </div>
                  <div className="fact">
                    <dt>{t('qboGoodUntil')}</dt>
                    <dd title={t('qboGoodUntilHint')}>{day(s.connection.refreshExpiresAt)}</dd>
                  </div>
                </dl>
                <div className="row-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => void check.refetch()} disabled={check.isFetching}>
                    {check.isFetching ? t('loading') : t('qboCheck')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={disconnect.isPending}
                    onClick={() => {
                      if (window.confirm(t('qboDisconnectConfirm'))) disconnect.mutate();
                    }}
                  >
                    {t('qboDisconnect')}
                  </button>
                </div>
                {disconnect.error ? <ErrorBox error={disconnect.error} /> : null}
              </section>
              <section className="panel stack-sm" data-testid="qbo-history">
                <h2>{t('qboHistory')}</h2>
                <p className="muted">{t('qboHistoryHint')}</p>
                {Object.keys(s.history.counts).length ? (
                  <dl className="totals-strip">
                    {Object.entries(s.history.counts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([k, n]) => (
                        <div key={k}>
                          <dt>{KINDS.has(k) ? t(`qboKind_${k}` as AppMessageKey) : k}</dt>
                          <dd>{n.toLocaleString(intlLocale)}</dd>
                        </div>
                      ))}
                  </dl>
                ) : (
                  <p className="muted">{t('qboNothingYet')}</p>
                )}
                {s.history.lastRun ? (
                  s.history.lastRun.ok ? (
                    <p className="muted">
                      {t('qboLastImport').replace('{when}', whenAt(s.history.lastRun.finishedAt, intlLocale))} · {s.history.lastRun.detail}
                    </p>
                  ) : (
                    <p className="warn-band">{t('qboImportFailed').replace('{why}', s.history.lastRun.detail ?? '')}</p>
                  )
                ) : null}
                <div className="row-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => pull.mutate(false)} disabled={pull.isPending || s.history.running}>
                    {s.history.running ? t('qboImporting') : t('qboImport')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    title={t('qboImportAllHint')}
                    disabled={pull.isPending || s.history.running}
                    onClick={() => {
                      if (window.confirm(t('qboImportAllHint'))) pull.mutate(true);
                    }}
                  >
                    {t('qboImportAll')}
                  </button>
                </div>
                <p>
                  <Link to="/qbo/matching">{t('qboOpenMatching')}</Link>
                </p>
                {s.history.startError ? <p className="warn-band">{s.history.startError}</p> : null}
                {pull.error ? <ErrorBox error={pull.error} /> : null}
              </section>
              <WriteCard />
              <PracticeCard />
              <MirrorCard />
              <SwitchCard />
              {check.error && !refused ? <ErrorBox error={check.error} onRetry={() => void check.refetch()} /> : null}
              {check.data ? (
                <section className="panel stack-sm" data-testid="qbo-check">
                  <h2>{t('qboInQuickBooks')}</h2>
                  <p className="muted">{t('qboCheckHint')}</p>
                  <dl className="totals-strip">
                    {(['customers', 'vendors', 'items', 'invoices', 'payments', 'bills', 'accounts'] as const).map((k) => (
                      <div key={k}>
                        <dt>{t(`qboCount_${k}`)}</dt>
                        <dd>{check.data.counts[k].toLocaleString(intlLocale)}</dd>
                      </div>
                    ))}
                  </dl>
                  {check.data.oldestInvoiceDate ? <p className="muted">{t('qboOldestInvoice').replace('{date}', formatDate(check.data.oldestInvoiceDate, intlLocale, 'date'))}</p> : null}
                </section>
              ) : null}
            </>
          ) : (
            <section className="panel stack-sm" data-testid="qbo-connect">
              <h2>{t('qboConnectTitle')}</h2>
              <p>{t('qboConnectHint')}</p>
              <fieldset className="choice">
                <legend className="label">{t('qboBooks')}</legend>
                {(['production', 'sandbox'] as const).map((e) => (
                  <label key={e} className="radio">
                    <input type="radio" name="qbo-env" value={e} checked={env === e} onChange={() => setEnv(e)} />
                    <span>
                      <strong>{t(`qboEnv_${e}`)}</strong>
                      <span className="row-sub"> {t(`qboEnvHint_${e}`)}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              <button type="button" className="btn btn-primary" onClick={() => connect.mutate(env)} disabled={connect.isPending}>
                {connect.isPending ? t('loading') : t('qboConnect')}
              </button>
              {connect.error ? <ErrorBox error={connect.error} /> : null}
              <p className="muted">
                {t('qboRedirectHint')} <code>{s.redirectUri}</code>
              </p>
            </section>
          )
        }
      </QueryView>
      <p className="muted">
        <Link to="/legal/privacy">{t('privacyTitle')}</Link> · <Link to="/legal/terms">{t('termsTitle')}</Link>
      </p>
    </div>
  );
}

/** The privacy policy and the terms Intuit asks an app to link to; public, no sign-in. */
export function LegalPage({ kind }: { kind: 'privacy' | 'terms' }) {
  const { t } = useLocale();
  const keys: AppMessageKey[] =
    kind === 'privacy' ? ['privacy1', 'privacy2', 'privacy3', 'privacy4', 'privacy5', 'privacy6'] : ['terms1', 'terms2', 'terms3', 'terms4', 'terms5'];
  return (
    <main className="legal-page">
      <h1>{kind === 'privacy' ? t('privacyTitle') : t('termsTitle')}</h1>
      <p className="muted">{t('legalUpdated')}</p>
      {keys.map((k) => (
        <p key={k}>{t(k)}</p>
      ))}
      <p>
        <Link to="/">{t('legalBack')}</Link>
      </p>
    </main>
  );
}

/**
 * QuickBooks' money in the app (decision 114): off until the owner turns it on; meanwhile a Preview of what it would
 * bring in. Once on: the last run, and every invoice whose balance differs from QuickBooks' (it should be none).
 */
function MirrorCard() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  // just turned on, QuickBooks' money comes in after the answer: the card looks again until the run is shown
  const [kicked, setKicked] = useState(0);
  const status = useQuery({
    queryKey: qk.qboMirror,
    queryFn: ({ signal }) => api.qboMirror(signal),
    // a run just asked for may not have started when the card first looks again: it keeps looking for a minute
    refetchInterval: (q) => (Date.now() - kicked < 60_000 ? 3_000 : comingIn(q.state.data) ? 15_000 : false),
  });
  const check = useQuery({ queryKey: qk.qboMirrorCheck, queryFn: ({ signal }) => api.qboMirrorCheck(signal), enabled: status.data?.on === true });
  const run = useMutation({
    mutationFn: () => api.qboMirrorRun(),
    onSuccess: () => {
      setKicked(Date.now());
      void qc.invalidateQueries({ queryKey: ['qbo'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
  const m = status.data;
  if (!m || !m.connected) return status.error ? <ErrorBox error={status.error} /> : null;
  const p = m.preview;
  const money = (v: number) => formatMoney(v, intlLocale);
  return (
    <section className="panel stack-sm" data-testid="qbo-mirror">
      <h2>{t('qboMirrorTitle')}</h2>
      <p className="muted">{m.on ? t('qboMirrorOnHint') : t('qboMirrorOffHint')}</p>
      {p ? (
        <dl className="totals-strip" data-testid="qbo-mirror-preview">
          <div>
            <dt>{t('qboMirrorInvoicesNew')}</dt>
            <dd>{(p.invoices.created + p.invoices.updated + p.invoices.voided + p.invoices.adopted).toLocaleString(intlLocale)}</dd>
          </div>
          <div>
            <dt>{t('qboMirrorInvoicesHeld')}</dt>
            <dd>{p.invoices.unchanged.toLocaleString(intlLocale)}</dd>
          </div>
          {p.invoices.adopted ? (
            <div data-testid="qbo-mirror-adopted">
              <dt>{t('qboMirrorAdopted')}</dt>
              <dd>{p.invoices.adopted.toLocaleString(intlLocale)}</dd>
            </div>
          ) : null}
          <div>
            <dt>{t('qboMirrorPaymentRows')}</dt>
            <dd>{p.payments.written.toLocaleString(intlLocale)}</dd>
          </div>
          <div className={p.skipped.invoices ? 'warn' : undefined}>
            <dt>{t('qboMirrorSkipped').replace('{n}', String(p.skipped.customers))}</dt>
            <dd>
              {p.skipped.invoices.toLocaleString(intlLocale)} · {money(p.skipped.openBalance)}
            </dd>
          </div>
        </dl>
      ) : null}
      {p && p.skipped.invoices ? (
        <p className="muted">
          {t('qboMirrorSkippedHint')} <Link to="/qbo/matching">{t('qboOpenMatching')}</Link>
        </p>
      ) : null}
      {p && p.problems.length ? (
        <details>
          <summary>{t('qboMirrorProblems').replace('{n}', String(p.problems.length))}</summary>
          <ul className="list">
            {p.problems.slice(0, 50).map((x, i) => (
              <li key={`${x.qboId}-${i}`} className="row-sub">
                {x.detail}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {m.last ? (
        <p className="muted">
          {t('qboMirrorLast').replace('{when}', whenAt(m.last.at, intlLocale))} · {m.last.invoices.created + m.last.invoices.updated + m.last.invoices.voided} · {m.last.payments.written}
        </p>
      ) : null}
      {m.on && check.data ? (
        check.data.length === 0 ? (
          <p className="note" data-testid="qbo-mirror-match">
            ✓ {t('qboMirrorAllMatch')}
          </p>
        ) : (
          <div className="stack-sm" data-testid="qbo-mirror-differ">
            <p className="warn-band">{t('qboMirrorDiffer').replace('{n}', String(check.data.length))}</p>
            <ul className="list">
              {check.data.slice(0, 50).map((d) => (
                <li key={d.invoiceId}>
                  <Link className="row" to={`/invoices/${d.invoiceId}`}>
                    <span className="row-main">
                      <span className="row-title">
                        {t('invoice')} {d.docNumber ?? ''} · {d.customerName}
                      </span>
                      <span className="row-sub">
                        QuickBooks {money(d.qboBalance)} · {t('qboMirrorApp')} {money(d.appBalance)}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )
      ) : null}
      {m.on && m.turnedOnAt ? <p className="muted">{t('qboMirrorTurnedOnAt').replace('{when}', whenAt(m.turnedOnAt, intlLocale))}</p> : null}
      {comingIn(m) ? (
        <p className="note" data-testid="qbo-mirror-coming">
          {m.running ? t('qboMirrorRunning') : t('qboTurnOnStarted')}
        </p>
      ) : null}
      {m.on && m.runError ? (
        <p className="warn-band" data-testid="qbo-mirror-error">
          {t('qboMirrorRunError').replace('{why}', m.runError)}
        </p>
      ) : null}
      {m.on && me?.role === 'owner' ? (
        <button type="button" className="btn btn-secondary btn-sm" disabled={run.isPending || m.running} onClick={() => run.mutate()}>
          {t('qboMirrorRun')}
        </button>
      ) : null}
      {run.error ? <ErrorBox error={run.error} /> : null}
      {!m.on && m.practice && me?.role === 'owner' ? <TurnOn status={m} /> : null}
    </section>
  );
}

/**
 * The app writes its invoices to QuickBooks (decision 185): the owner turns it on from a day, pauses it, and sees what
 * is waiting, what went and what QuickBooks refused (with Try again). The office sees it.
 */
function WriteCard() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const owner = me?.role === 'owner';
  const status = useQuery({ queryKey: qk.qboWrite, queryFn: ({ signal }) => api.qboWrite(signal), refetchInterval: (q) => (q.state.data?.counts.waiting ? 10_000 : false) });
  // no day until the owner picks one: turning on makes invoices real from it
  const [from, setFrom] = useState('');
  const done = (w: QboWriteStatus) => {
    qc.setQueryData(qk.qboWrite, w);
    void qc.invalidateQueries({ queryKey: ['invoices'] });
  };
  const turnOn = useMutation({ mutationFn: () => api.qboWriteTurnOn(from), onSuccess: done });
  const pause = useMutation({ mutationFn: (paused: boolean) => api.qboWritePause(paused), onSuccess: done });
  const turnOff = useMutation({ mutationFn: () => api.qboWriteTurnOff(), onSuccess: done });
  const retry = useMutation({ mutationFn: (id: string) => api.qboWriteRetry(id), onSuccess: done });
  const doneThere = useMutation({ mutationFn: (id: string) => api.qboWriteDoneThere(id), onSuccess: done });
  const send = useMutation({ mutationFn: () => api.qboWriteSendNow(), onSettled: () => void qc.invalidateQueries({ queryKey: qk.qboWrite }) });
  const w = status.data;
  if (!w) return status.error ? <ErrorBox error={status.error} /> : null;
  const error = turnOn.error ?? pause.error ?? turnOff.error ?? retry.error ?? doneThere.error ?? send.error;
  return (
    <section className="panel stack-sm" data-testid="qbo-write">
      <h2>{t('qboWriteTitle')}</h2>
      {w.on ? null : (
        <p className="warn-band" data-testid="qbo-write-off">
          <strong>{t('qboWriteOffBand')}</strong>
        </p>
      )}
      <p className="muted">{t('qboWriteHint')}</p>
      {w.on ? (
        <>
          <p className={w.paused ? 'warn-band' : 'ok-band'} data-testid="qbo-write-state">
            {w.paused ? t('qboWritePaused') : w.real ? t('qboWriteOn') : t('qboWriteFrom').replace('{date}', w.from ? formatDate(w.from, intlLocale, 'date') : '—')}
            {w.paused && w.pausedWhy ? ` ${w.pausedWhy}` : ''}
            {w.turnedOnAt ? ` · ${t('qboWriteTurnedOn').replace('{when}', whenAt(w.turnedOnAt, intlLocale)).replace('{who}', w.turnedOnByName ?? '—')}` : ''}
          </p>
          {w.otherServer ? <p className="warn-band">{t('qboWriteOtherServer')}</p> : null}
          {w.reconnect ? <p className="warn-band">{t('qboWriteReconnect')}</p> : null}
          <dl className="totals-strip" data-testid="qbo-write-counts">
            <div>
              <dt>{t('qboWriteCountWaiting')}</dt>
              <dd>{w.counts.waiting.toLocaleString(intlLocale)}</dd>
            </div>
            <div>
              <dt>{t('qboWriteCountSent')}</dt>
              <dd>{w.counts.sent.toLocaleString(intlLocale)}</dd>
            </div>
            <div className={w.counts.failed ? 'warn' : undefined}>
              <dt>{t('qboWriteCountFailed')}</dt>
              <dd>{w.counts.failed.toLocaleString(intlLocale)}</dd>
            </div>
          </dl>
          {w.lastSentAt ? <p className="muted">{t('qboWriteLastSent').replace('{when}', whenAt(w.lastSentAt, intlLocale))}</p> : null}
          {w.failures.length ? (
            <ul className="list" data-testid="qbo-write-failures">
              {w.failures.map((f) => (
                <li key={f.id} className="count-row stack-sm">
                  <span className="row-main">
                    {f.kind === 'Invoice' && f.invoiceId ? (
                      <Link className="row-title" to={`/invoices/${f.invoiceId}`}>
                        {t('invoice')} {f.invoiceNo ?? ''} · {f.customerName ?? ''}
                      </Link>
                    ) : (
                      <Link className="row-title" to={f.customerId ? `/customers/${f.customerId}/statement` : '#'}>
                        {f.kind === 'Payment' ? t('qboWriteKindPayment') : t('qboWriteKindCredit')} {f.amount === null ? '' : formatMoney(f.amount, intlLocale)} · {f.customerName ?? ''}
                        {f.invoiceNo ? ` · ${t('invoice')} ${f.invoiceNo}` : ''}
                      </Link>
                    )}
                    <span className="row-sub">{f.error ?? f.needs ?? ''}</span>
                  </span>
                  {/* under the words, so a phone keeps them whole */}
                  {owner ? (
                    <div className="contact-actions">
                      <button type="button" className="btn btn-secondary btn-sm" disabled={retry.isPending} onClick={() => retry.mutate(f.id)}>
                        {t('qboWriteRetry')}
                      </button>
                      {f.kind === 'Payment' && DONE_THERE_NEEDS.includes(f.needs ?? '') ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={doneThere.isPending}
                          onClick={() => {
                            if (window.confirm(t('qboWriteDoneThereConfirm'))) doneThere.mutate(f.id);
                          }}
                        >
                          {t('qboWriteDoneThere')}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {owner ? (
            <div className="row-actions">
              <button type="button" className="btn btn-primary btn-sm" disabled={send.isPending || w.paused} onClick={() => send.mutate()}>
                {send.isPending ? t('loading') : t('qboWriteSendNow')}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={pause.isPending} onClick={() => pause.mutate(!w.paused)}>
                {w.paused ? t('qboWriteResume') : t('qboWritePause')}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={turnOff.isPending}
                onClick={() => {
                  if (window.confirm(t('qboWriteTurnOffConfirm'))) turnOff.mutate();
                }}
              >
                {t('qboWriteTurnOff')}
              </button>
            </div>
          ) : null}
          {send.data ? <p className="muted">{t('qboWriteSentNow').replace('{sent}', String(send.data.sent)).replace('{failed}', String(send.data.failed)).replace('{waiting}', String(send.data.waiting))}</p> : null}
        </>
      ) : owner ? (
        <div className="stack-sm" data-testid="qbo-write-turn-on">
          <ol className="turn-on-list">
            <li>{t('qboWriteStepNumbers')}</li>
            <li>{t('qboWriteStepCredits')}</li>
            <li>{t('qboWriteStepSales')}</li>
            <li>{t('qboWriteStepMoney')}</li>
          </ol>
          {w.blocked ? <p className="warn-band" data-testid="qbo-write-blocked">{w.blocked}</p> : null}
          <label className="field">
            <span>{t('qboWriteFromLabel')}</span>
            <input type="date" value={from} min={todayIso()} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!!w.blocked || !from || turnOn.isPending}
            onClick={() => {
              if (window.confirm(t('qboWriteConfirm').replace('{date}', formatDate(from, intlLocale, 'date')))) turnOn.mutate();
            }}
          >
            {t('qboWriteTurnOn')}
          </button>
        </div>
      ) : (
        <p className="muted">{t('qboWriteOff')}</p>
      )}
      {error ? <ErrorBox error={error} /> : null}
    </section>
  );
}

/** Practice (decisions 140, 187): what was typed while QuickBooks holds the books, and the owner's Delete practice. */
function PracticeCard() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const counts = useQuery({ queryKey: qk.practice, queryFn: ({ signal }) => api.practice(signal) });
  const [typed, setTyped] = useState('');
  const clear = useMutation({
    mutationFn: () => api.practiceClear(),
    onSuccess: () => {
      setTyped('');
      void qc.invalidateQueries();
    },
  });
  const p = counts.data;
  if (!p) return counts.error ? <ErrorBox error={counts.error} /> : null;
  const money = (v: number) => formatMoney(v, intlLocale);
  const any = p.invoices + p.payments + p.credits + p.orders > 0;
  return (
    <section className="panel stack-sm" data-testid="practice-card">
      <h2>{t('practiceCardTitle')}</h2>
      <p className="muted">{t('practiceCardHint')}</p>
      <dl className="totals-strip">
        <div>
          <dt>{t('practiceInvoices')}</dt>
          <dd>{p.invoices.toLocaleString(intlLocale)} · {money(p.invoiceTotal)}</dd>
        </div>
        <div>
          <dt>{t('practicePayments')}</dt>
          <dd>{p.payments.toLocaleString(intlLocale)} · {money(p.paymentTotal)}</dd>
        </div>
        <div>
          <dt>{t('practiceCredits')}</dt>
          <dd>{p.credits.toLocaleString(intlLocale)} · {money(p.creditTotal)}</dd>
        </div>
      </dl>
      {p.ordersBack ? <p className="muted">{t('practiceOrdersBack').replace('{n}', String(p.ordersBack))}</p> : null}
      {me?.role === 'owner' && any ? (
        <div className="stack-sm">
          <label className="field">
            <span>{t('practiceType')}</span>
            <input type="text" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
          </label>
          <button type="button" className="btn btn-warn" disabled={typed.trim() !== 'DELETE PRACTICE' || clear.isPending} onClick={() => clear.mutate()}>
            {t('practiceDelete')}
          </button>
        </div>
      ) : null}
      {clear.data ? <p className="ok-band">{t('practiceDeleted').replace('{n}', String(clear.data.invoices)).replace('{p}', String(clear.data.payments)).replace('{c}', String(clear.data.credits))}</p> : null}
      {clear.error ? <ErrorBox error={clear.error} /> : null}
    </section>
  );
}

/** The switch day (plan C12, decision 197): why it cannot be done yet, what it does, and the owner's typed Switch. */
function SwitchCard() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const [backup, setBackup] = useState(false);
  const [typed, setTyped] = useState('');
  const q = useQuery({ queryKey: qk.booksSwitch, queryFn: ({ signal }) => api.booksSwitch(signal), refetchInterval: (query) => (query.state.data?.running ? 4000 : false) });
  const go = useMutation({
    mutationFn: () => api.booksSwitchStart(),
    onSuccess: () => {
      setTyped('');
      void qc.invalidateQueries({ queryKey: ['qbo'] });
    },
  });
  const s = q.data;
  if (!s) return q.error ? <ErrorBox error={q.error} /> : null;
  const money = (v: number) => formatMoney(v, intlLocale);
  const date = (d: string | null) => (d ? formatDate(d, intlLocale, 'date') : '—');
  if (s.switchedAt) {
    return (
      <section className="panel stack-sm" data-testid="books-switch">
        <h2>{t('switchTitle')}</h2>
        <p className="ok-band">{t('switchDone').replace('{when}', whenAt(s.switchedAt, intlLocale))}</p>
        {/* the owner turns them on, once each is set up on the server */}
        {me?.role === 'owner' && !s.ready.squareLive ? <p className="note">{s.ready.squareConfigured ? t('switchTurnOnSquare') : t('switchSquareNotSet')} <Link to="/settings/payments">{t('paymentsTitle')}</Link></p> : null}
        {me?.role === 'owner' && !s.ready.emailLive ? <p className="note">{s.ready.emailConfigured ? t('switchTurnOnEmail') : t('switchEmailNotSet')} <Link to="/settings/email">{t('emailSettingsTitle')}</Link></p> : null}
        {s.enteredInQuickBooks && s.enteredInQuickBooks.invoices + s.enteredInQuickBooks.payments + s.enteredInQuickBooks.credits > 0 ? (
          <p className="warn-band" data-testid="books-switch-entered">
            {t('switchEnteredThere')
              .replace('{i}', String(s.enteredInQuickBooks.invoices))
              .replace('{p}', String(s.enteredInQuickBooks.payments))
              .replace('{c}', String(s.enteredInQuickBooks.credits))
              .replace('{docs}', s.enteredInQuickBooks.docNumbers.join(', ') || '—')}
          </p>
        ) : null}
      </section>
    );
  }
  const ready = s.blocked.length === 0 && !s.running && backup && typed.trim() === 'SWITCH';
  return (
    <section className="panel stack-sm" data-testid="books-switch">
      <h2>{t('switchTitle')}</h2>
      <p className="muted">{t('switchHint')}</p>
      <ul className="turn-on-list">
        <li>{t('switchPractice').replace('{n}', String(s.practice.invoices)).replace('{total}', money(s.practice.invoiceTotal)).replace('{p}', String(s.practice.payments)).replace('{c}', String(s.practice.credits))}</li>
        {s.practice.ordersBack ? <li>{t('switchOrdersStay').replace('{n}', String(s.practice.ordersBack))}</li> : null}
        <li>{t('switchLedger').replace('{from}', date(s.ledgerFrom)).replace('{to}', date(s.proposedFrom))}</li>
        {s.nextInvoiceNo ? <li>{t('switchNumbers').replace('{n}', String(s.nextInvoiceNo))}</li> : null}
        <li>{t('switchAfter')}</li>
      </ul>
      <SwitchReady ready={s.ready} />
      {s.running ? <p className="note">{t('switchRunning')}</p> : null}
      {s.error && !s.running ? <p className="warn-band" data-testid="books-switch-error">{t('switchStopped').replace('{why}', s.error)}</p> : null}
      {s.blocked.length ? (
        <div className="warn-band" data-testid="books-switch-blocked">
          <p>{t('switchBlocked')}</p>
          <ul>
            {s.blocked.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="ok-band">{t('switchReady')}</p>
      )}
      {me?.role === 'owner' ? (
        <div className="stack-sm">
          <label className="check">
            <input type="checkbox" checked={backup} onChange={(e) => setBackup(e.target.checked)} />
            <span>{t('switchBackup')}</span>
          </label>
          <label className="field">
            <span>{t('switchType')}</span>
            <input type="text" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
          </label>
          <button type="button" className="btn btn-warn" disabled={!ready || go.isPending} onClick={() => go.mutate()}>
            {t('switchButton')}
          </button>
        </div>
      ) : null}
      {go.error ? <ErrorBox error={go.error} /> : null}
    </section>
  );
}

/** The day's own checks (plan C16), said, never blocking: every customer against QuickBooks' A/R aging read now, the
 *  stock count, Square and email ready to turn on after the switch. */
function SwitchReady({ ready: r }: { ready: BooksSwitchStatus['ready'] }) {
  const { t, intlLocale } = useLocale();
  const aging = useMutation({ mutationFn: () => api.qboBalances(undefined, 'report') });
  const money = (v: number) => formatMoney(v, intlLocale);
  // counted within the day before (the count is the night before the switch)
  const fresh = !!r.stockCountedAt && Date.now() - Date.parse(r.stockCountedAt) < 36 * 3_600_000;
  const mark = (ok: boolean) => <span aria-hidden="true">{ok ? '✓' : '○'}</span>;
  const a = aging.data;
  return (
    <div className="stack-sm" data-testid="books-switch-ready">
      <h3>{t('switchReadyTitle')}</h3>
      <ul className="ready-list">
        <li>
          {mark(!!a && a.connected && a.totals.customers > 0 && a.totals.differ === 0)}{' '}
          {a
            ? t('switchAgingResult').replace('{n}', String(a.totals.differ)).replace('{of}', String(a.totals.customers)).replace('{total}', money(a.reportTotal ?? 0))
            : t('switchAgingAsk')}{' '}
          <button type="button" className="btn btn-sm" disabled={aging.isPending} onClick={() => aging.mutate()}>
            {aging.isPending ? t('switchAgingReading') : t('switchAgingButton')}
          </button>{' '}
          <Link to="/qbo/matching?tab=balances">{t('switchAgingOpen')}</Link>
          {aging.error ? <ErrorBox error={aging.error} /> : null}
        </li>
        <li className={fresh ? undefined : 'warn'}>
          {mark(fresh)} {r.stockCountedAt ? t('switchCounted').replace('{when}', whenAt(r.stockCountedAt, intlLocale)).replace('{n}', String(r.countedProducts)) : t('switchNotCounted')}{' '}
          <Link to="/stock">{t('countTitle')}</Link>
        </li>
        <li>
          {mark(r.squareConfigured && r.squareTested)} {!r.squareConfigured ? t('switchSquareNotSet') : r.squareTested ? t('switchSquareReady') : t('switchSquareNotTested')}{' '}
          <Link to="/settings/payments">{t('paymentsTitle')}</Link>
        </li>
        <li>
          {mark(r.emailConfigured)} {r.emailConfigured ? t('switchEmailReady') : t('switchEmailNotSet')} <Link to="/settings/email">{t('emailSettingsTitle')}</Link>
        </li>
      </ul>
    </div>
  );
}

/** Just turned on and the first run not shown yet (nor failed): QuickBooks' money is coming in. */
function comingIn(m: QboMirrorStatus | undefined): boolean {
  return !!m?.on && (m.running || (!!m.turnedOnAt && !m.runError && (!m.last || m.last.at < m.turnedOnAt)));
}

/** The owner turns the mirror on (decision 126): what it clears, the ledger start, a backup first, typed. */
function TurnOn({ status }: { status: QboMirrorStatus }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [backup, setBackup] = useState(false);
  const [typed, setTyped] = useState('');
  const go = useMutation({
    mutationFn: () => api.qboMirrorTurnOn(),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
  const p = status.practice!;
  const money = (v: number) => formatMoney(v, intlLocale);
  const ready = !status.blocked && backup && typed.trim() === 'TURN ON';
  return (
    <div className="stack-sm" data-testid="qbo-turn-on">
      <h3>{t('qboTurnOnTitle')}</h3>
      <p>{t('qboTurnOnHint')}</p>
      <ul className="turn-on-list">
        <li>{t('qboTurnOnInvoices').replace('{n}', String(p.invoices)).replace('{total}', money(p.invoiceTotal))}</li>
        {p.sheetsInvoices ? <li>{t('qboTurnOnSheets').replace('{n}', String(p.sheetsInvoices))}</li> : null}
        {p.sheetsStayVoid ? <li className="warn">{t('qboTurnOnSheetsStay').replace('{n}', String(p.sheetsStayVoid))}</li> : null}
        <li>{t('qboTurnOnPayments').replace('{n}', String(p.payments)).replace('{total}', money(p.paymentTotal))}</li>
        <li>{t('qboTurnOnCredits').replace('{n}', String(p.credits)).replace('{total}', money(p.creditTotal))}</li>
        <li>{t('qboTurnOnOpening').replace('{n}', String(p.openingCustomers))}</li>
        <li>{t('qboTurnOnLedger').replace('{from}', p.ledgerFrom ? formatDate(p.ledgerFrom, intlLocale, 'date') : '—').replace('{to}', p.proposedFrom ? formatDate(p.proposedFrom, intlLocale, 'date') : '—')}</li>
        <li>{t('qboTurnOnAfter')}</li>
      </ul>
      {status.blocked ? <p className="warn-band" data-testid="qbo-turn-on-blocked">{status.blocked}</p> : null}
      <label className="check">
        <input type="checkbox" checked={backup} onChange={(e) => setBackup(e.target.checked)} />
        <span>{t('qboTurnOnBackup')}</span>
      </label>
      <label className="field">
        <span>{t('qboTurnOnType')}</span>
        <input type="text" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </label>
      <button type="button" className="btn btn-primary" disabled={!ready || go.isPending} onClick={() => go.mutate()}>
        {t('qboTurnOnButton')}
      </button>
      {go.error ? <ErrorBox error={go.error} /> : null}
    </div>
  );
}

